import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api/axios';
import { Plus, Trash2, Edit2, Eye, Copy, BarChart2, X, GripVertical, ChevronDown, ChevronUp, Mail, Settings, ExternalLink, FileText, CheckCircle2, LayoutTemplate } from 'lucide-react';
import * as XLSX from 'xlsx';

const FIELD_TYPES = [
  { type: 'text', label: 'Kısa Metin', icon: '📝' },
  { type: 'textarea', label: 'Uzun Metin', icon: '📄' },
  { type: 'email', label: 'E-posta', icon: '📧' },
  { type: 'phone', label: 'Telefon', icon: '📞' },
  { type: 'number', label: 'Sayı', icon: '🔢' },
  { type: 'date', label: 'Tarih', icon: '📅' },
  { type: 'select', label: 'Açılır Liste', icon: '📋' },
  { type: 'radio', label: 'Tekli Seçim', icon: '🔘' },
  { type: 'checkbox', label: 'Çoklu Seçim', icon: '☑️' },
  { type: 'rating', label: 'Yıldız Puanı', icon: '⭐' },
  { type: 'divider', label: 'Ayraç', icon: '➖' },
  { type: 'heading', label: 'Başlık', icon: '🔤' },
];

const FieldPreview = ({ field }) => {
  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #d0d5dd', borderRadius: '10px', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#f9fafb', color: '#1e293b' };
  switch (field.field_type) {
    case 'text': case 'email': case 'phone': case 'number':
      return <input type={field.field_type} placeholder={field.placeholder || field.label} style={inputStyle} readOnly />;
    case 'textarea':
      return <textarea placeholder={field.placeholder || field.label} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} readOnly />;
    case 'date':
      return <input type="date" style={inputStyle} readOnly />;
    case 'select':
      return <select style={inputStyle}><option>{field.placeholder || 'Seçiniz...'}</option>{(field.options || []).map((o, i) => <option key={i}>{o}</option>)}</select>;
    case 'radio':
      return <div className="d-flex flex-col gap-2">{(field.options || ['Seçenek 1', 'Seçenek 2']).map((o, i) => <label key={i} className="d-flex align-center gap-2 cursor-pointer text-muted"><input type="radio" readOnly /> {o}</label>)}</div>;
    case 'checkbox':
      return <div className="d-flex flex-col gap-2">{(field.options || ['Seçenek 1', 'Seçenek 2']).map((o, i) => <label key={i} className="d-flex align-center gap-2 cursor-pointer text-muted"><input type="checkbox" readOnly /> {o}</label>)}</div>;
    case 'rating':
      return <div className="d-flex gap-1 text-orange" style={{ fontSize: 24 }}>{[1,2,3,4,5].map(i => <span key={i}>★</span>)}</div>;
    case 'divider':
      return <hr style={{ border: 'none', borderTop: '2px solid #e2e8f0', margin: '8px 0' }} />;
    case 'heading':
      return <div className="font-bold text-dark" style={{ fontSize: 18 }}>{field.label}</div>;
    default:
      return <input placeholder={field.label} style={inputStyle} readOnly />;
  }
};

const FieldEditor = ({ field, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [expanded, setExpanded] = useState(true);
  const [optionInput, setOptionInput] = useState('');

  const addOption = () => {
    if (!optionInput.trim()) return;
    onChange({ ...field, options: [...(field.options || []), optionInput.trim()] });
    setOptionInput('');
  };

  const removeOption = (i) => {
    onChange({ ...field, options: field.options.filter((_, idx) => idx !== i) });
  };

  const hasOptions = ['select', 'radio', 'checkbox'].includes(field.field_type);
  const hasPlaceholder = ['text', 'textarea', 'email', 'phone', 'number', 'select'].includes(field.field_type);
  const isDecorative = ['divider', 'heading'].includes(field.field_type);

  return (
    <div className="field-editor-card">
      <div className="field-editor-header" onClick={() => setExpanded(!expanded)}>
        <GripVertical size={16} className="text-muted cursor-move" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 18 }}>{FIELD_TYPES.find(f => f.type === field.field_type)?.icon}</span>
        <span className="font-bold text-dark flex-1">{field.label || 'Alan İsmi'}</span>
        {field.is_required && <span className="badge badge-mandatory">Zorunlu</span>}
        <div className="d-flex align-center gap-1">
          <button onClick={e => { e.stopPropagation(); onMoveUp(); }} className="btn-icon-mini"><ChevronUp size={16} /></button>
          <button onClick={e => { e.stopPropagation(); onMoveDown(); }} className="btn-icon-mini"><ChevronDown size={16} /></button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }} className="btn-icon-mini text-red"><Trash2 size={14} /></button>
        </div>
      </div>

      {expanded && (
        <div className="field-editor-body p-4">
          <div className="form-grid mb-3">
            <div className="form-group">
              <label className="form-label">{isDecorative ? 'İÇERİK' : 'ALAN ETİKETİ'}</label>
              <input value={field.label} onChange={e => onChange({ ...field, label: e.target.value })} className="form-input" />
            </div>
            {hasPlaceholder && (
              <div className="form-group">
                <label className="form-label">YER TUTUCU (PLACEHOLDER)</label>
                <input value={field.placeholder || ''} onChange={e => onChange({ ...field, placeholder: e.target.value })} className="form-input" />
              </div>
            )}
          </div>

          {!isDecorative && (
            <div className="form-group mb-3">
              <label className="form-label">YARDIM METNİ</label>
              <input value={field.help_text || ''} onChange={e => onChange({ ...field, help_text: e.target.value })} className="form-input" />
            </div>
          )}

          {hasOptions && (
            <div className="form-group mb-3">
              <label className="form-label">SEÇENEKLER</label>
              <div className="d-flex flex-col gap-2 mb-2">
                {(field.options || []).map((opt, i) => (
                  <div key={i} className="d-flex align-center gap-2">
                    <input value={opt} onChange={e => { const opts = [...field.options]; opts[i] = e.target.value; onChange({ ...field, options: opts }); }} className="form-input flex-1" style={{ padding: '8px 12px' }} />
                    <button onClick={() => removeOption(i)} className="btn-icon-mini text-red"><X size={16} /></button>
                  </div>
                ))}
              </div>
              <div className="d-flex align-center gap-2">
                <input value={optionInput} onChange={e => setOptionInput(e.target.value)} placeholder="Yeni seçenek ekle..." onKeyDown={e => e.key === 'Enter' && addOption()} className="form-input flex-1" style={{ padding: '8px 12px' }} />
                <button onClick={addOption} className="btn-primary" style={{ padding: '8px 16px' }}>Ekle</button>
              </div>
            </div>
          )}

          {!isDecorative && (
            <label className="d-flex align-center gap-2 cursor-pointer mt-3 user-select-none">
              <input type="checkbox" checked={field.is_required} onChange={e => onChange({ ...field, is_required: e.target.checked })} style={{ width: 16, height: 16 }} />
              <span className="font-medium text-dark">Bu alanın doldurulması zorunlu olsun</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default function AdminForms() {
  const [forms, setForms] = useState([]);
  const [view, setView] = useState('list');
  const [editingForm, setEditingForm] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', slug: '', success_message: 'Yanıtınız alındı. Teşekkürler!', email_notification: false, notification_email: '', status: 'active' });
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');
  const [submissions, setSubmissions] = useState([]);
  const [viewingForm, setViewingForm] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [deleteSubmission, setDeleteSubmission] = useState(null);
  const [deletingSubmission, setDeletingSubmission] = useState(false);

  const load = () => api.get('/forms').then(r => setForms(r.data));
  useEffect(() => { load(); }, []);

  const newForm = () => {
    setEditingForm(null);
    setFormData({ title: 'Yeni Form Başlığı', description: '', slug: '', success_message: 'Yanıtınız başarıyla alındı. Teşekkür ederiz!', email_notification: false, notification_email: '', status: 'active' });
    setFields([]);
    setActiveTab('builder');
    setView('edit');
  };

  const editForm = async (form) => {
    const r = await api.get('/forms/' + form.id);
    setEditingForm(r.data);
    setFormData({ title: r.data.title, description: r.data.description || '', slug: r.data.slug || '', success_message: r.data.success_message, email_notification: r.data.email_notification, notification_email: r.data.notification_email || '', status: r.data.status });
    setFields(r.data.fields.map(f => ({ ...f, options: f.options || [] })));
    setActiveTab('builder');
    setView('edit');
  };

  const viewSubmissions = async (form) => {
    setViewingForm(form);
    const r = await api.get('/forms/' + form.id + '/submissions');
    setSubmissions(r.data);
    const fr = await api.get('/forms/' + form.id);
    setViewingForm({ ...form, fields: fr.data.fields });
    setView('submissions');
  };

  const addField = (type) => {
    const newField = {
      id: 'new-' + Date.now(),
      field_type: type,
      label: FIELD_TYPES.find(f => f.type === type)?.label || 'Alan',
      placeholder: '',
      help_text: '',
      is_required: false,
      options: ['select', 'radio', 'checkbox'].includes(type) ? ['Seçenek 1', 'Seçenek 2'] : [],
      settings: {},
      order_index: fields.length,
    };
    setFields([...fields, newField]);
  };

  const updateField = (index, updated) => {
    setFields(fields.map((f, i) => i === index ? updated : f));
  };

  const deleteField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const moveField = (index, dir) => {
    const newFields = [...fields];
    const target = index + dir;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setFields(newFields);
  };

  const save = async () => {
    if (!formData.title.trim()) return;
    setSaving(true);
    try {
      let saved;
      if (editingForm) {
        const r = await api.put('/forms/' + editingForm.id, { ...formData, fields });
        saved = r.data;
      } else {
        const r = await api.post('/forms', { ...formData, fields });
        saved = r.data;
        setEditingForm(saved);
        setActiveTab('settings');
      }
      load();
    } catch(e) { alert('Hata: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  const deleteForm = async (id) => {
    if (!window.confirm('Bu formu silmek istediğinize emin misiniz?')) return;
    await api.delete('/forms/' + id);
    load();
  };

  const confirmDeleteSubmission = async () => {
    if (!deleteSubmission) return;
    setDeletingSubmission(true);
    try {
      await api.delete('/forms/submissions/' + deleteSubmission.id);
      const r = await api.get('/forms/' + viewingForm.id + '/submissions');
      setSubmissions(r.data);
      setDeleteSubmission(null);
    } catch (e) {
      alert('Yanıt silinemedi: ' + (e.response?.data?.error || e.message));
    } finally {
      setDeletingSubmission(false);
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(window.location.origin + '/form/' + slug);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formLink = editingForm ? window.location.origin + '/form/' + editingForm.slug : '';

  const exportSubmissionsToExcel = () => {
    if (!submissions.length || !viewingForm) { alert('Aktarılacak yanıt bulunamadı.'); return; }
    const formFields = (viewingForm.fields || []).filter(f => !['divider', 'heading'].includes(f.field_type));
    const headers = ['Yanıt No', 'Tarih', ...formFields.map((field, index) => field.label || `Alan ${index + 1}`)];
    const rows = submissions.map((sub, i) => [
      submissions.length - i,
      sub.submitted_at ? new Date(sub.submitted_at).toLocaleString('tr-TR') : '',
      ...formFields.map(field => {
        const value = sub.data?.[field.id];
        if (Array.isArray(value)) return value.join(', ');
        if (value === null || value === undefined) return '';
        return String(value);
      })
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = headers.map((header, colIndex) => {
      const maxLength = Math.max(String(header).length, ...rows.map(row => String(row[colIndex] ?? '').length));
      return { wch: Math.min(Math.max(maxLength + 2, 15), 50) };
    });
    worksheet['!freeze'] = { ySplit: 1 };
    worksheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${rows.length + 1}` };
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Yanıtlar');
    const safeName = (viewingForm.title || 'Form Yanıtları').replace(/[\\/:*?"<>|]/g, '').trim();
    XLSX.writeFile(workbook, `${safeName} - Yanıtlar.xlsx`);
  };

  // Tüm ekranlarda çalışması için CSS'i değişkene alıp her return'de çağırıyoruz.
  const pageStyles = (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

      * { -webkit-font-smoothing: antialiased; font-family: 'Poppins', sans-serif; }

      .page-container { animation: fadeSlideUp 0.4s ease-out; }
      @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

      /* Typography & Utils */
      .text-muted { color: #6b7280; }
      .text-dark { color: #111827; }
      .text-primary { color: #2563eb; }
      .text-blue { color: #2563eb; }
      .text-red { color: #e30613; }
      .text-green { color: #16a34a; }
      .text-orange { color: #f59e0b; }
      
      .font-medium { font-weight: 500; }
      .font-bold { font-weight: 700; }
      .fw-normal { font-weight: 400; }
      
      .w-full { width: 100%; }
      .h-full { height: 100%; }
      .h-fit { height: fit-content; }
      .flex-1 { flex: 1; }
      .min-w-60 { min-width: 60px; }
      .min-w-200 { min-width: 200px; }
      
      .d-flex { display: flex; }
      .flex-col { flex-direction: column; }
      .align-center { align-items: center; }
      .align-start { align-items: flex-start; }
      .justify-between { justify-content: space-between; }
      .justify-center { justify-content: center; }
      
      .gap-1 { gap: 4px; }
      .gap-2 { gap: 8px; }
      .gap-3 { gap: 12px; }
      .gap-4 { gap: 16px; }
      
      .m-0 { margin: 0; }
      .mb-1 { margin-bottom: 4px; }
      .mb-2 { margin-bottom: 8px; }
      .mb-3 { margin-bottom: 12px; }
      .mb-4 { margin-bottom: 16px; }
      .mt-1 { margin-top: 4px; }
      .mt-2 { margin-top: 8px; }
      .mt-3 { margin-top: 12px; }
      .mt-4 { margin-top: 16px; }
      .p-0 { padding: 0; }
      .p-3 { padding: 12px; }
      .p-4 { padding: 16px; }
      .py-2 { padding-top: 8px; padding-bottom: 8px; }
      .py-3 { padding-top: 12px; padding-bottom: 12px; }
      .px-3 { padding-left: 12px; padding-right: 12px; }
      .pb-5 { padding-bottom: 24px; }
      .pl-5 { padding-left: 24px; }
      .mr-1 { margin-right: 4px; }

      .text-small { font-size: 12.5px; }
      .text-truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .cursor-pointer { cursor: pointer; }
      .cursor-move { cursor: move; }
      .user-select-none { user-select: none; }
      
      .border-r { border-right: 1px solid #e5e7eb; }
      .rounded-lg { border-radius: 8px; }
      
      .bg-white { background: #ffffff; }
      .bg-gray-50 { background: #f8fafc; }
      .bg-gray-100 { background: #f1f5f9; }
      .bg-blue-light { background: #eff6ff; }
      .bg-green-light { background: #f0fdf4; }
      .bg-primary { background: linear-gradient(135deg, #2563eb, #1d4ed8); }
      .bg-gray { background: #cbd5e1; }

      /* Header */
      .page-header { margin-bottom: 24px; }
      .page-title { margin: 0; font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -0.5px; }
      .page-subtitle { margin: 0; font-size: 13.5px; color: #6b7280; margin-top: 4px;}

      /* Cards & Form */
      .content-card { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); overflow: hidden; }
      
      .form-group { margin-bottom: 0; }
      .form-label { display: block; margin-bottom: 8px; font-weight: 600; font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;}
      .form-input { width: 100%; padding: 10px 14px; border: 1px solid #d0d5dd; border-radius: 10px; font-size: 14px; outline: none; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; box-sizing: border-box; background: white; color: #1e293b;}
      .form-input:focus { border-color: #2563eb; box-shadow: 0 0 0 4px #eff6ff; }

      /* Buttons */
      .btn-primary { background: #2563eb; color: white; border: 1px solid #2563eb; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
      .btn-primary:hover:not(:disabled) { background: #1d4ed8; border-color: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2); }
      .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; background: #94a3b8; border-color: #94a3b8;}
      
      .btn-red { background: #e30613; color: white; border: 1px solid #e30613; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
      .btn-red:hover:not(:disabled) { background: #ba000a; border-color: #ba000a; transform: translateY(-1px); }
      .btn-red:disabled { opacity: 0.6; cursor: not-allowed; background: #9ca3af; border-color: #9ca3af;}

      .btn-success { background: #16a34a; color: white; border: 1px solid #16a34a; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s; }
      .btn-success:hover { background: #15803d; border-color: #15803d; transform: translateY(-1px); }
      
      .btn-danger { background: #fef2f2; color: #e30613; border: 1px solid #fecaca; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s; }
      .btn-danger:hover:not(:disabled) { background: #fee2e2; border-color: #fca5a5; }
      .btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

      .btn-outline { background: #ffffff; border: 1px solid #d0d5dd; color: #344054; border-radius: 10px; padding: 10px 18px; cursor: pointer; font-size: 13.5px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; box-shadow: 0 1px 2px rgba(16,24,40,0.05);}
      .btn-outline:hover { background: #f9fafb; border-color: #98a2b3; color: #111827;}
      
      .btn-outline-blue { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; border-radius: 10px; padding: 10px 18px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; }
      .btn-outline-blue:hover { background: #dbeafe; border-color: #93c5fd; }
      
      .btn-outline-green { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; border-radius: 10px; padding: 10px 18px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; }
      .btn-outline-green:hover { background: #dcfce7; border-color: #86efac; }

      .btn-cancel { padding: 10px 20px; border: 1px solid #d0d5dd; background: #ffffff; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 14px; color: #344054; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
      .btn-cancel:hover { background: #f9fafb; border-color: #98a2b3; }
      
      .btn-icon-danger { background: #fef2f2; color: #ef4444; border: 1px solid #fee2e2; border-radius: 10px; padding: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
      .btn-icon-danger:hover { background: #fee2e2; border-color: #fca5a5; }
      
      .btn-icon-mini { background: transparent; color: #94a3b8; border: none; border-radius: 6px; padding: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
      .btn-icon-mini:hover { background: #e2e8f0; color: #1e293b; }
      .btn-icon-mini.text-red:hover { background: #fee2e2; color: #e30613; }

      /* Tabs */
      .tabs-container { display: inline-flex; background: #f3f4f6; border-radius: 12px; padding: 4px; margin-bottom: 24px; flex-wrap: wrap;}
      .tab-btn { padding: 8px 20px; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; gap: 8px; background: transparent; color: #6b7280; transition: all 0.2s; }
      .tab-btn:hover:not(.active) { color: #111827; }
      .tab-btn.active { background: #ffffff; color: #2563eb; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

      /* Badges */
      .badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 700; white-space: nowrap;}
      .status-active { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
      .status-passive { background: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb; }
      .badge-mandatory { background: #fef2f2; color: #ef4444; font-size: 10px; padding: 2px 8px; }

      /* Empty States */
      .empty-state-card { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); padding: 60px 20px; text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center;}
      .border-dashed { border: 2px dashed #cbd5e1; box-shadow: none; background: #f8fafc;}

      /* Form List View */
      .grid-layout-forms { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
      .form-card { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); overflow: hidden; transition: transform 0.2s; }
      .form-card:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(16, 24, 40, 0.08); border-color: #d0d5dd;}
      .form-card-topbar { height: 5px; width: 100%; }
      
      .form-link-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 10px; display: flex; align-items: center; gap: 8px; }
      .form-link-text { font-size: 11.5px; color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .btn-link-copy { background: #e2e8f0; color: #334155; border: none; border-radius: 6px; padding: 4px 10px; font-size: 11px; font-weight: 700; cursor: pointer; transition: all 0.2s; flex-shrink: 0;}
      .btn-link-copy:hover { background: #cbd5e1; }
      .btn-link-external { background: transparent; color: #64748b; border: none; padding: 4px; display: flex; align-items: center; justify-content: center; border-radius: 6px; transition: all 0.2s; flex-shrink: 0;}
      .btn-link-external:hover { background: #e2e8f0; color: #1e293b; }

      /* Form Builder View */
      .editor-top-bar { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); padding: 12px 20px;}
      .editor-title-input { font-size: 20px; font-weight: 800; color: #1e293b; border: none; border-bottom: 2px solid transparent; padding: 4px 0; outline: none; background: transparent; transition: border-color 0.2s; width: 100%;}
      .editor-title-input:focus { border-bottom-color: #2563eb; }
      .editor-title-input::placeholder { color: #94a3b8; }
      
      .grid-layout-builder { display: grid; grid-template-columns: 1fr; gap: 16px; }
      @media (min-width: 1024px) { .grid-layout-builder { grid-template-columns: 280px 1fr; } }
      
      .btn-field-add { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border: 1px solid #e2e8f0; border-radius: 10px; background: white; cursor: pointer; font-size: 13.5px; font-weight: 600; color: #334155; transition: all 0.2s;}
      .btn-field-add:hover { background: #eff6ff; border-color: #93c5fd; color: #1e3a8a; transform: translateX(2px);}

      .field-editor-card { background: white; border-radius: 12px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
      .field-editor-header { padding: 12px 16px; display: flex; align-items: center; gap: 12px; background: #f8fafc; border-bottom: 1px solid transparent; transition: background 0.2s;}
      .field-editor-header:hover { background: #f1f5f9; }
      .field-editor-body { border-top: 1px solid #e2e8f0; }

      .email-notify-card { padding: 16px; background: #f8fafc; border-radius: 12px; border: 1px solid #e2e8f0; transition: all 0.2s; }
      .email-notify-card.active { background: #eff6ff; border-color: #bfdbfe; }

      /* Preview Canvas */
      .preview-canvas { max-width: 680px; margin: 0 auto; background: white; border-radius: 20px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.08); border: 1px solid #eaecf0;}
      .preview-header { background: linear-gradient(135deg, #1a1a2e, #e30613); padding: 32px 40px; color: white;}
      .preview-title { margin: 0; font-size: 24px; font-weight: 800; line-height: 1.3;}
      .preview-desc { margin: 12px 0 0; font-size: 14.5px; line-height: 1.6; opacity: 0.9;}
      .preview-body { padding: 32px 40px; }
      .preview-label { display: block; margin-bottom: 8px; font-weight: 700; font-size: 14px; color: #1e293b; }
      .preview-help { font-size: 12px; color: #64748b; margin-top: 6px; }

      /* Submissions View */
      .submission-card { background: #ffffff; border-radius: 14px; border: 1px solid #eaecf0; overflow: hidden; box-shadow: 0 2px 4px rgba(16,24,40,0.02);}
      .submission-header { padding: 14px 20px; background: #f8fafc; border-bottom: 1px solid #eaecf0; display: flex; justify-content: space-between; align-items: center;}
      .submission-row { display: flex; flex-direction: column; gap: 4px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #f1f5f9; }
      .submission-row:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }
      @media (min-width: 640px) { .submission-row { flex-direction: row; gap: 16px; } }
      .submission-label { font-weight: 700; font-size: 13.5px; color: #334155; min-width: 200px; }
      .submission-value { font-size: 14px; color: #1e293b; flex: 1; word-break: break-word;}

      /* Modals */
      .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
      .modal-container { background: #ffffff; border-radius: 20px; width: 100%; box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08); animation: modalPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); display: flex; flex-direction: column; overflow: hidden;}
      @keyframes modalPop { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .modal-title { margin: 0; font-size: 19px; font-weight: 700; color: #111827; }
      
      .danger-icon-large { width: 64px; height: 64px; border-radius: 50%; background: #fef2f2; color: #ef4444; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    `}</style>
  );

  // ===================== YANITLAR (SUBMISSIONS) GÖRÜNÜMÜ =====================
  if (view === 'submissions') return (
    <AdminLayout>
      <div className="page-container">
        <div className="page-header d-flex justify-between align-center flex-wrap gap-3">
          <div>
            <h2 className="page-title">{viewingForm?.title} <span className="text-muted fw-normal">— Yanıtlar</span></h2>
            <p className="page-subtitle">Toplam {submissions.length} yanıt listeleniyor.</p>
          </div>
          <div className="d-flex gap-2">
            {submissions.length > 0 && (
              <button onClick={exportSubmissionsToExcel} className="btn-success">
                <FileText size={16} /> Excel'e Aktar
              </button>
            )}
            <button onClick={() => setView('list')} className="btn-outline">
              Geri Dön
            </button>
          </div>
        </div>

        {submissions.length === 0 ? (
          <div className="empty-state-card">
            <Mail size={48} className="text-gray-300 mb-3" />
            <h3 className="text-dark font-bold mb-1">Henüz yanıt yok</h3>
            <p className="text-muted">Bu form için henüz kimse yanıt göndermemiş.</p>
          </div>
        ) : (
          <div className="d-flex flex-col gap-3">
            {submissions.map((sub, i) => (
              <div key={sub.id} className="submission-card">
                <div className="submission-header">
                  <span className="font-bold text-dark">Yanıt #{submissions.length - i}</span>
                  <div className="d-flex align-center gap-3">
                    <span className="text-small text-muted">{new Date(sub.submitted_at).toLocaleString('tr-TR')}</span>
                    <button onClick={() => setDeleteSubmission(sub)} className="btn-icon-danger" title="Yanıtı sil">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="submission-body p-4">
                  {viewingForm?.fields?.filter(f => !['divider','heading'].includes(f.field_type)).map(field => (
                    <div key={field.id} className="submission-row">
                      <div className="submission-label">{field.label}</div>
                      <div className="submission-value">
                        {Array.isArray(sub.data[field.id]) ? sub.data[field.id].join(', ') : sub.data[field.id] || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Silme Modalı */}
        {deleteSubmission && (
          <div className="modal-overlay" style={{ zIndex: 9999 }}>
            <div className="modal-container text-center" style={{ maxWidth: 420, padding: 32 }}>
              <div className="danger-icon-large"><Trash2 size={28} /></div>
              <h3 className="modal-title mb-2">Yanıtı Sil</h3>
              <p className="text-muted mb-4">Bu formu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
              <div className="d-flex gap-2">
                <button onClick={() => !deletingSubmission && setDeleteSubmission(null)} className="btn-cancel flex-1">Vazgeç</button>
                <button onClick={confirmDeleteSubmission} disabled={deletingSubmission} className="btn-danger flex-1">
                  {deletingSubmission ? 'Siliniyor...' : 'Evet, Sil'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {pageStyles}
    </AdminLayout>
  );

  // ===================== FORM OLUŞTURUCU / DÜZENLE (EDIT) GÖRÜNÜMÜ =====================
  if (view === 'edit') return (
    <AdminLayout>
      <div className="page-container">
        {/* Üst Bar */}
        <div className="editor-top-bar d-flex justify-between align-center flex-wrap gap-3 mb-4">
          <input 
            value={formData.title} 
            onChange={e => setFormData({...formData, title: e.target.value})}
            placeholder="Form başlığını girin..."
            className="editor-title-input flex-1 min-w-200" 
          />
          <div className="d-flex gap-2 align-center">
            {editingForm && (
              <button onClick={() => copyLink(editingForm.slug)} className="btn-outline-blue">
                {copied ? <CheckCircle2 size={16}/> : <Copy size={16}/>} 
                {copied ? 'Kopyalandı!' : 'Linki Kopyala'}
              </button>
            )}
            {editingForm && (
              <a href={'/form/' + editingForm.slug} target="_blank" rel="noreferrer" className="btn-outline-green">
                <ExternalLink size={16} /> Önizle
              </a>
            )}
            <button onClick={() => setView('list')} className="btn-outline">İptal</button>
            <button onClick={save} disabled={saving} className="btn-red">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>

        {/* Tablar */}
        <div className="tabs-container">
          {[['builder', LayoutTemplate, 'Form Oluşturucu'], ['settings', Settings, 'Ayarlar'], ['preview', Eye, 'Önizleme']].map(([key, Icon, label]) => (
            <button key={key} onClick={() => setActiveTab(key)} className={`tab-btn ${activeTab === key ? 'active' : ''}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* Builder Sekmesi */}
        {activeTab === 'builder' && (
          <div className="grid-layout-builder">
            {/* Sol: Alanlar */}
            <div className="content-card p-4 h-fit">
              <div className="font-bold text-dark mb-3">Alan Ekle</div>
              <div className="d-flex flex-col gap-2">
                {FIELD_TYPES.map(ft => (
                  <button key={ft.type} onClick={() => addField(ft.type)} className="btn-field-add">
                    <span style={{ fontSize: 18 }}>{ft.icon}</span>
                    <span className="flex-1 text-left">{ft.label}</span>
                    <Plus size={14} className="text-muted" />
                  </button>
                ))}
              </div>
            </div>

            {/* Sağ: Editör */}
            <div className="builder-canvas">
              {fields.length === 0 ? (
                <div className="empty-state-card border-dashed">
                  <LayoutTemplate size={48} className="text-gray-300 mb-3" />
                  <h3 className="text-dark font-bold mb-1">Formunuz boş</h3>
                  <p className="text-muted">Sol taraftan alan ekleyerek formunuzu oluşturmaya başlayın.</p>
                </div>
              ) : (
                <div className="d-flex flex-col gap-3 pb-5">
                  {fields.map((field, index) => (
                    <FieldEditor 
                      key={field.id || index} 
                      field={field}
                      onChange={(updated) => updateField(index, updated)}
                      onDelete={() => deleteField(index)}
                      onMoveUp={() => moveField(index, -1)}
                      onMoveDown={() => moveField(index, 1)} 
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Sekmesi */}
        {activeTab === 'settings' && (
          <div className="content-card p-4" style={{ maxWidth: 640 }}>
            <div className="form-group mb-4">
              <label className="form-label">Form URL (Slug)</label>
              <div className="d-flex align-center form-input p-0 overflow-hidden">
                <span className="bg-gray-50 text-muted px-3 py-2 border-r text-small h-full d-flex align-center">/form/</span>
                <input 
                  value={formData.slug} 
                  onChange={e => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})}
                  placeholder="ornek-form-adi"
                  className="flex-1 py-2 px-3 border-none outline-none text-dark" 
                />
              </div>
              <div className="text-small text-muted mt-1">Sadece küçük harf, rakam ve tire kullanın. Boş bırakırsanız otomatik oluşturulur.</div>
            </div>
            
            <div className="form-group mb-4">
              <label className="form-label">Form Açıklaması</label>
              <textarea 
                value={formData.description} 
                onChange={e => setFormData({...formData, description: e.target.value})} 
                rows={3}
                className="form-input" 
                style={{ resize: 'vertical' }}
              />
            </div>
            
            <div className="form-group mb-4">
              <label className="form-label">Başarı Mesajı (Form gönderildikten sonra)</label>
              <textarea 
                value={formData.success_message} 
                onChange={e => setFormData({...formData, success_message: e.target.value})} 
                rows={2}
                className="form-input" 
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="form-group mb-4">
              <label className="form-label">Durum</label>
              <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="form-input">
                <option value="active">Aktif</option>
                <option value="inactive">Pasif</option>
              </select>
            </div>

            <div className={`email-notify-card ${formData.email_notification ? 'active' : ''}`}>
              <label className="d-flex align-center gap-3 cursor-pointer user-select-none">
                <input type="checkbox" checked={formData.email_notification} onChange={e => setFormData({...formData, email_notification: e.target.checked})} style={{ width: 16, height: 16 }} />
                <div>
                  <div className="font-bold text-dark">E-posta Bildirimi</div>
                  <div className="text-small text-muted">Yeni yanıt geldiğinde e-posta ile haberdar ol.</div>
                </div>
              </label>
              
              {formData.email_notification && (
                <div className="mt-3 pl-5">
                  <input 
                    type="email" 
                    value={formData.notification_email} 
                    onChange={e => setFormData({...formData, notification_email: e.target.value})}
                    placeholder="bildirim@sirket.com"
                    className="form-input" 
                  />
                </div>
              )}
            </div>

            {editingForm && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="font-bold text-dark mb-2 text-small">Kısa Link</div>
                <div className="d-flex gap-2">
                  <input value={formLink} readOnly className="form-input flex-1 bg-white text-muted" />
                  <button onClick={() => copyLink(editingForm.slug)} className="btn-primary">
                    {copied ? <CheckCircle2 size={16}/> : <Copy size={16}/>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview Sekmesi */}
        {activeTab === 'preview' && (
          <div className="preview-canvas">
            <div className="preview-header">
              <h2 className="preview-title">{formData.title}</h2>
              {formData.description && <p className="preview-desc">{formData.description}</p>}
            </div>
            <div className="preview-body">
              {fields.map((field, i) => (
                <div key={i} className="mb-4">
                  {!['divider','heading'].includes(field.field_type) && (
                    <label className="preview-label">
                      {field.label} {field.is_required && <span className="text-red">*</span>}
                    </label>
                  )}
                  <FieldPreview field={field} />
                  {field.help_text && <div className="preview-help">{field.help_text}</div>}
                </div>
              ))}
              {fields.length > 0 && (
                <button className="btn-red w-full mt-2 py-3" style={{ fontSize: 16 }}>Gönder</button>
              )}
            </div>
          </div>
        )}
      </div>
      {pageStyles}
    </AdminLayout>
  );

  // ===================== ANA LİSTE (LIST) GÖRÜNÜMÜ =====================
  return (
    <AdminLayout>
      <div className="page-container">
        <div className="page-header d-flex justify-between align-center flex-wrap gap-3">
          <div className="d-flex align-center gap-2">
            <LayoutTemplate size={24} className="text-primary" />
            <h2 className="page-title m-0">Form Yöneticisi</h2>
          </div>
          <button onClick={newForm} className="btn-primary">
            <Plus size={16} strokeWidth={3} /> Yeni Form
          </button>
        </div>

        {forms.length === 0 ? (
          <div className="empty-state-card">
            <LayoutTemplate size={48} className="text-gray-300 mb-3" />
            <h3 className="text-dark font-bold mb-1">Henüz form yok</h3>
            <p className="text-muted mb-4">Anketler, sınavlar veya geri bildirim toplamak için ilk formunuzu oluşturun.</p>
            <button onClick={newForm} className="btn-primary">Form Oluştur</button>
          </div>
        ) : (
          <div className="grid-layout-forms">
            {forms.map(form => (
              <div key={form.id} className="form-card">
                <div className={`form-card-topbar ${form.status === 'active' ? 'bg-primary' : 'bg-gray'}`} />
                <div className="p-4">
                  <div className="d-flex justify-between align-start mb-2 gap-2">
                    <h3 className="font-bold text-dark m-0" style={{ fontSize: 16, lineHeight: 1.3 }}>{form.title}</h3>
                    <span className={`badge ${form.status === 'active' ? 'status-active' : 'status-passive'}`}>
                      {form.status === 'active' ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                  
                  <div className="text-small text-muted mb-3 text-truncate" style={{ minHeight: 18 }}>
                    {form.description || 'Açıklama yok'}
                  </div>
                  
                  <div className="d-flex align-center gap-3 mb-4">
                    <div className="bg-blue-light text-blue px-3 py-2 rounded-lg text-center min-w-60">
                      <div className="font-bold" style={{ fontSize: 20 }}>{form.submission_count || 0}</div>
                      <div className="text-small font-medium">Yanıt</div>
                    </div>
                  </div>
                  
                  <div className="form-link-box mb-3">
                    <span className="text-muted mr-1">🔗</span>
                    <span className="form-link-text flex-1">{window.location.origin}/form/{form.slug}</span>
                    <button onClick={() => copyLink(form.slug)} className="btn-link-copy">
                      {copied ? '✓' : 'Kopya'}
                    </button>
                    <a href={'/form/' + form.slug} target="_blank" rel="noreferrer" className="btn-link-external">
                      <ExternalLink size={14} />
                    </a>
                  </div>

                  <div className="d-flex gap-2">
                    <button onClick={() => editForm(form)} className="btn-outline-blue flex-1 py-2">
                      <Edit2 size={14} /> Düzenle
                    </button>
                    <button onClick={() => viewSubmissions(form)} className="btn-outline-green flex-1 py-2">
                      <BarChart2 size={14} /> Yanıtlar
                    </button>
                    <button onClick={() => deleteForm(form.id)} className="btn-icon-danger">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {pageStyles}
    </AdminLayout>
  );
}
